import React, { useState, useEffect } from 'react';
import {
    collection,
    doc,
    updateDoc,
    deleteDoc,
    query,
    where,
    getDoc,
    getDocs,
    db,
} from '../../Firebase';
import { Modal, Button } from 'react-bootstrap';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";
import '../css/Dialog.css';
import '../css/pdf.css';
import '../css/history.css';
import { FaThumbsUp, FaThumbsDown, FaRegTrashAlt } from 'react-icons/fa';

function GeminiModal({ knowledge, geminiModalVisible, closeGeminiModal, basePath, projectName }) {
    const [qas, setQas] = useState([]);
    const [deletedItem, setDeletedItem] = useState(0);
    const project = projectName.split('/').join('_');   // e.g. "Folder_Subfolder"
    const projectPath = projectName.split('/')[0];      // top-level folder
    const [isLoading, setIsLoading] = useState(false);

    // ---------- FETCH HELPERS ----------
    const normalizeProjectLabel = (s) => {
        if (!s) return '';
        const lastSeg = String(s).split('/').filter(Boolean).slice(-1)[0];
        return lastSeg.replace(/_/g, ' ').trim();
    };

    const fetchQAsByProject = async (project, knowledge) => {
        const qasColRef = collection(db, basePath, 'llm');
        let q;
        if (knowledge !== 'generalAll') {
            const start = project;
            const end = project + '\uf8ff';
            q = query(qasColRef, where('project', '>=', start), where('project', '<', end));
        } else {
            q = query(qasColRef);
        }
        const querySnapshot = await getDocs(q);
        const out = [];
        querySnapshot.forEach((snap) => {
            out.push({ id: snap.id, ...snap.data() });
        });
        return out;
    };

    // SPECIFIC: add source tags so we can group & delete accurately
    const fetchQAsSpecificPath = async (projectPath, projectDocId) => {
        const qasColRef = collection(db, basePath, 'contracts', projectPath, 'qa');
        const pathSnap = await getDocs(qasColRef);
        let pathQAs = [];

        pathSnap.forEach((snap) => {
            // Only take docs for this project (your existing filter)
            if (snap.id.startsWith(projectDocId)) {
                const qasArray = snap.data().qas || [];
                const label = normalizeProjectLabel(snap.id);
                const mapped = qasArray.map((qa, index) => ({
                    id: `${snap.id}-${index}`,     // unique id
                    _qaDocId: snap.id,             // doc id inside .../qa/<docId>
                    _projectPath: projectPath,     // contracts/<projectPath>/qa
                    projectLabel: label,           // pretty label for grouping
                    ...qa,                         // includes query/prediction/createdAt/feedback
                }));
                pathQAs = pathQAs.concat(mapped);
            }
        });

        return pathQAs;
    };

    // SPECIFIC ALL: tag each QA with its source doc id + path
    const fetchQAsSpecificAll = async () => {
        const contractsRef = collection(db, basePath, 'contracts');
        const contractsSnapshot = await getDocs(contractsRef);
        const allQAs = [];

        for (const contractDoc of contractsSnapshot.docs) {
            const qaRef = collection(db, basePath, 'contracts', contractDoc.id, 'qa');
            const qaSnapshot = await getDocs(qaRef);

            for (const qaDoc of qaSnapshot.docs) {
                const qaData = qaDoc.data();
                const qasArray = qaData.qas || [];
                const label = normalizeProjectLabel(qaDoc.id);

                const withIds = qasArray.map((qa, index) => ({
                    id: `${contractDoc.id}-${qaDoc.id}-${index}`, // global unique id
                    _projectPath: contractDoc.id,                  // contracts/<_projectPath>/qa/<_qaDocId>
                    _qaDocId: qaDoc.id,                            // the QA doc id (usually your project id)
                    projectLabel: label,                           // pretty label for grouping
                    ...qa,
                }));

                allQAs.push(...withIds);
            }
        }

        return allQAs;
    };

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            let fetched = [];

            try {
                if (knowledge === 'general' || knowledge === 'generalAll') {
                    fetched = await fetchQAsByProject(project, knowledge);
                } else if (knowledge === 'specific') {
                    fetched = await fetchQAsSpecificPath(projectPath, project);
                } else if (knowledge === 'specificAll') {
                    fetched = await fetchQAsSpecificAll();
                }
            } catch (err) {
                console.error('Error fetching data:', err);
            } finally {
                setQas(fetched);
                setIsLoading(false);
            }
        };

        if (geminiModalVisible) {
            fetchData();
        }
    }, [knowledge, geminiModalVisible, deletedItem, project, projectPath, basePath]);

    // ---------- DELETION ----------
    const deleteGenQA = async (qaId) => {
        const qasDocRef = doc(db, basePath, 'llm', qaId);
        try {
            await deleteDoc(qasDocRef);
            setDeletedItem((c) => c + 1);
        } catch (error) {
            console.error('Error removing document: ', error);
        }
    };

    // For specific/specificAll, we now pass the *actual* path per item
    const deleteSpecQA = async (qaId, projPath, qaDocId) => {
        const qasDocRef = doc(db, basePath, 'contracts', projPath, 'qa', qaDocId);
        const docSnap = await getDoc(qasDocRef);
        if (!docSnap.exists()) {
            console.log('No such document!');
            return;
        }
        const currentQAs = docSnap.data().qas || [];
        const updated = currentQAs.filter((qa, index) => `${qaDocId}-${index}` !== qaId);
        await updateDoc(qasDocRef, { qas: updated });
        setDeletedItem((c) => c + 1);
    };

    const handleCloseModal = () => {
        setQas([]);
        closeGeminiModal();
    };

    // ---------- GROUPING / LABEL HELPERS ----------
    const toDateSafe = (d) => (d?.toDate ? d.toDate() : d instanceof Date ? d : new Date(d));

    const monthKey = (d) => {
        const dt = toDateSafe(d);
        const y = dt.getFullYear();
        const m = String(dt.getMonth() + 1).padStart(2, '0');
        return `${y}-${m}`; // YYYY-MM
    };

    const monthLabelFromKey = (ym) => {
        const [y, m] = ym.split('-').map(Number);
        const dt = new Date(y, m - 1, 1);
        return dt.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
    };

    const dayLabel = (d) =>
        toDateSafe(d).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: '2-digit' });

    const groupBy = (arr, keyFn) =>
        arr.reduce((acc, item) => {
            const k = keyFn(item);
            (acc[k] ||= []).push(item);
            return acc;
        }, {});

    const getProjectKey = (qa) => {
        // Prefer the explicit label we added during fetch
        if (qa.projectLabel) return qa.projectLabel;

        // Else try known fields
        const candidates = [
            qa.project,
            qa.projectName,
            qa.project_path,
            qa.projectPath,
            qa.folder,
            qa.ref,
            qa.collectionPath,
            qa.collection,
            qa.basePath,
            qa.path,
            qa._qaDocId,
        ].filter(Boolean);

        let key = candidates.find((v) => typeof v === 'string' && v.trim());
        if (key) return normalizeProjectLabel(key);

        // As a last resort, try to derive from id (first segment before the first hyphen could be projectish)
        if (typeof qa.id === 'string') {
            const maybe = qa.id.split('-')[0];
            if (maybe) return normalizeProjectLabel(maybe);
        }
        return 'Unknown project';
    };

    return (
        <Modal
            show={geminiModalVisible}
            onHide={handleCloseModal}
            scrollable={true}
            className="modalPDF"
            backdrop="static"
            dialogClassName="modalPDF-dialog"
        >
            <Modal.Header className="modalPDF-header">
                <Modal.Title className="modalPDF-title">
                    <span className="modalPDF-title1">Gemini-{knowledge}</span>
                    <span className="modalPDF-title2">{projectName}</span>
                    <span className="modalPDF-title3">&#x1F50E;&#xFE0E; CTRL-F</span>
                </Modal.Title>
            </Modal.Header>

            <Modal.Body className="modalPDF-body">
                {isLoading ? (
                    <div className="modalGemini-qas">
                        <div className="wave-text">Loading Q&A history...</div>
                    </div>
                ) : knowledge === 'general' || knowledge === 'generalAll' ? (
                    <div className="modalPDF-document">
                        {qas.length > 0 ? (
                            // GROUP BY MONTH (desc)
                            Object.entries(groupBy(qas, (qa) => monthKey(qa.createdAt)))
                                .sort(([a], [b]) => (a < b ? 1 : -1))
                                .map(([ym, items]) => (
                                    <details key={ym} className="qa-group">
                                        <summary className="qa-summary">
                                            <span>{monthLabelFromKey(ym)}</span>
                                            <span className="qa-count">{items.length}</span>
                                        </summary>

                                        <div className="qa-list">
                                            {items
                                                .sort((a, b) => toDateSafe(b.createdAt) - toDateSafe(a.createdAt))
                                                .map((qa) => (
                                                    <div key={qa.id} className="modalGemini-qas">
                                                        <ReactMarkdown skipHtml className="chat-text bold geminiQ">
                                                            {qa.question}
                                                        </ReactMarkdown>

                                                        <ReactMarkdown skipHtml className="chat-text geminiA">
                                                            {`Date: ${dayLabel(qa.createdAt)} - Ref: ${normalizeProjectLabel(
                                                                qa.project || qa.projectLabel || ''
                                                            )}`}
                                                        </ReactMarkdown>

                                                        <div className="chat-entry">
                                                            <ReactMarkdown
                                                                skipHtml
                                                                remarkPlugins={[remarkMath, remarkGfm]}
                                                                rehypePlugins={[rehypeKatex]}
                                                                className="chat-text geminiA">
                                                                {qa.answer}
                                                            </ReactMarkdown>
                                                        </div>

                                                        <div
                                                            className="feedback-icons"
                                                            title="Delete Q&A"
                                                            onClick={() => deleteGenQA(qa.id)}
                                                            style={{ alignSelf: 'flex-end' }}
                                                        >
                                                            <FaRegTrashAlt size={22} color="var(--orange)" />
                                                        </div>
                                                    </div>
                                                ))}
                                        </div>
                                    </details>
                                ))
                        ) : (
                            <div className="modalGemini-qas">
                                <p className="chat-text geminiA">No Q&As found in this path.</p>
                            </div>
                        )}
                    </div>
                ) : knowledge === 'specific' || knowledge === 'specificAll' ? (
                    <div className="modalPDF-document">
                        {qas.length > 0 ? (
                            // GROUP BY PROJECT (A-Z)
                            Object.entries(groupBy(qas, (qa) => getProjectKey(qa)))
                                .sort(([a], [b]) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
                                .map(([projectGroup, items]) => (
                                    <details key={projectGroup} className="qa-group">
                                        <summary className="qa-summary">
                                            <span>{projectGroup}</span>
                                            <span className="qa-count">{items.length}</span>
                                        </summary>

                                        <div className="qa-list">
                                            {items
                                                .sort((a, b) => toDateSafe(b.createdAt) - toDateSafe(a.createdAt))
                                                .map((qa) => (
                                                    <div key={qa.id} className="modalGemini-qas">
                                                        <ReactMarkdown skipHtml className="chat-text bold geminiQ">
                                                            {qa.query ?? qa.question ?? '(no title)'}
                                                        </ReactMarkdown>

                                                        {qa.createdAt && (
                                                            <ReactMarkdown skipHtml className="chat-text geminiA">
                                                                {`Date: ${dayLabel(qa.createdAt)}`}
                                                            </ReactMarkdown>
                                                        )}

                                                        <div className="chat-entry">
                                                            <ReactMarkdown skipHtml remarkPlugins={[remarkGfm]} className="chat-text geminiA">
                                                                {qa.prediction ?? qa.answer ?? ''}
                                                            </ReactMarkdown>
                                                        </div>

                                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                            <div className="feedback-icons" style={{ marginLeft: '20px' }}>
                                                                {qa.feedback === 'thumbsUp' ? (
                                                                    <FaThumbsUp size={22} color="var(--orange)" />
                                                                ) : qa.feedback === 'thumbsDown' ? (
                                                                    <FaThumbsDown size={22} color="var(--orange)" />
                                                                ) : null}
                                                            </div>

                                                            <div
                                                                className="feedback-icons"
                                                                title="Delete Q&A"
                                                                onClick={() =>
                                                                    deleteSpecQA(
                                                                        qa.id,
                                                                        qa._projectPath || projectPath, // precise per item (specificAll) or fallback (specific)
                                                                        qa._qaDocId || project          // QA doc id per item or current project
                                                                    )
                                                                }
                                                            >
                                                                <FaRegTrashAlt size={22} color="var(--orange)" />
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                        </div>
                                    </details>
                                ))
                        ) : (
                            <div className="modalGemini-qas">
                                <p className="chat-text geminiA">No Q&As found in this path.</p>
                            </div>
                        )}
                    </div>
                ) : (
                    <p />
                )}
            </Modal.Body>

            <Modal.Footer className="modalPDF-footer">
                <Button className="confirmButtonPDF" variant="dark" onClick={handleCloseModal} disabled={isLoading}>
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export default GeminiModal;
